import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { bookings as seedBookings, quotes as seedQuotes } from '../data/bookings';
import { threads as seedThreads } from '../data/threads';
import { tickets as seedTickets } from '../data/support';
import { customer } from '../data/customer';
import { quoteFromDraft } from '../utils/pricing';
import type { QuoteDraft } from '../utils/pricing';
import type { Booking, Quote } from '../types/booking';
import type { Message, Thread } from '../types/chat';
import type { Ticket, TicketCategory } from '../types/support';
import type { Chef, Fulfillment } from '../types/chef';
let counter = 0;
const uid = (prefix: string) => `${prefix}-${Date.now().toString(36)}-${counter++}`;

/** Amounts written into timelines and system messages are stored already formatted, in baht. */
const baht = (amount: number) => `฿${Math.round(amount).toLocaleString('en-US')}`;
export interface RequestInput {
  chefId: string;
  occasion: string;
  date: string;
  time: string;
  guests: number;
  fulfillment: Fulfillment;
  menuId?: string;
  note: string;
}
interface AppDataValue {
  threads: Thread[];
  bookings: Booking[];
  quotes: Quote[];
  tickets: Ticket[];
  savedChefIds: string[];
  threadById: (id?: string) => Thread | undefined;
  bookingById: (id?: string) => Booking | undefined;
  quoteById: (id?: string) => Quote | undefined;
  quoteVersions: (bookingId: string) => Quote[];
  sendMessage: (threadId: string, body: string, role: 'chef' | 'customer') => void;
  sendQuote: (chef: Chef, threadId: string, draft: QuoteDraft) => void;
  acceptQuote: (quoteId: string) => void;
  requestChanges: (quoteId: string, note: string) => void;
  declineQuote: (quoteId: string, reason: string) => void;
  createRequest: (input: RequestInput) => {
    threadId: string;
    bookingId: string;
  };
  markRead: (threadId: string, role: 'chef' | 'customer') => void;
  toggleSaved: (chefId: string) => void;
  createTicket: (input: {
    subject: string;
    category: TicketCategory;
    body: string;
    bookingRef?: string;
  }) => string;
  replyTicket: (ticketId: string, body: string) => void;
  cancelBooking: (bookingId: string, reason: string, refund: number) => void;
  addReceipt: (bookingId: string, label: string, amount: number) => void;
}
const AppDataContext = createContext<AppDataValue | null>(null);
export function AppDataProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [threads, setThreads] = useState<Thread[]>(seedThreads);
  const [bookings, setBookings] = useState<Booking[]>(seedBookings);
  const [quotes, setQuotes] = useState<Quote[]>(seedQuotes);
  const [tickets, setTickets] = useState<Ticket[]>(seedTickets);
  const [savedChefIds, setSavedChefIds] = useState<string[]>(customer.savedChefIds);
  const appendMessage = useCallback((threadId: string, message: Omit<Message, 'threadId'>) => {
    setThreads(prev => prev.map(thread => thread.id === threadId ? {
      ...thread,
      lastAt: message.at,
      unreadForChef: message.role === 'customer' ? thread.unreadForChef + 1 : thread.unreadForChef,
      unreadForCustomer: message.role === 'chef' ? thread.unreadForCustomer + 1 : thread.unreadForCustomer,
      messages: [...thread.messages, {
        ...message,
        threadId
      }]
    } : thread));
  }, []);
  const sendMessage = useCallback((threadId: string, body: string, role: 'chef' | 'customer') => {
    appendMessage(threadId, {
      id: uid('m'),
      role,
      kind: 'text',
      body,
      language: 'en-TH',
      at: new Date().toISOString(),
      read: true
    });
  }, [appendMessage]);
  const sendQuote = useCallback((chef: Chef, threadId: string, draft: QuoteDraft) => {
    const thread = threads.find(t => t.id === threadId);
    if (!thread) return;
    const bookingId = thread.bookingId ?? uid('bk');
    const version = quotes.filter(q => q.bookingId === bookingId).length + 1;
    const quote = quoteFromDraft(chef, draft, {
      id: uid('q'),
      version,
      bookingId,
      customerId: thread.customerId,
      expiresAt: new Date(Date.now() + 48 * 3600 * 1000).toISOString()
    });
    const at = new Date().toISOString();
    setQuotes(prev => [...prev.map(q => q.bookingId === bookingId && q.status === 'pending' ? {
      ...q,
      status: 'superseded' as const
    } : q), quote]);
    setBookings(prev => prev.map(b => b.id === bookingId ? {
      ...b,
      status: 'quoted',
      quoteId: quote.id,
      menuName: quote.menuName,
      total: quote.total,
      guests: quote.guests,
      date: quote.date,
      time: quote.time,
      timeline: [...b.timeline, {
        status: version > 1 ? 'quote_revised' : 'quoted',
        at,
        note: `Quote v${version} sent — ${baht(quote.total)}`
      }]
    } : b));
    appendMessage(threadId, {
      id: uid('m'),
      role: 'chef',
      kind: 'quote',
      body: version > 1 ? `Updated quote — version ${version}.` : 'Here is your quote.',
      language: 'en-TH',
      at,
      quoteId: quote.id,
      read: true
    });
  }, [appendMessage, quotes, threads]);
  const acceptQuote = useCallback((quoteId: string) => {
    const quote = quotes.find(q => q.id === quoteId);
    if (!quote) return;
    const at = new Date().toISOString();
    setQuotes(prev => prev.map(q => q.id === quoteId ? {
      ...q,
      status: 'accepted'
    } : q));
    setBookings(prev => prev.map(b => b.id === quote.bookingId ? {
      ...b,
      status: 'confirmed',
      total: quote.total,
      quoteId: quote.id,
      timeline: [...b.timeline, {
        status: 'confirmed',
        at,
        note: `Deposit of ${baht(quote.depositDue)} authorized`
      }]
    } : b));
    const thread = threads.find(t => t.bookingId === quote.bookingId);
    if (thread) {
      appendMessage(thread.id, {
        id: uid('m'),
        role: 'system',
        kind: 'system',
        body: `Quote accepted. Deposit of ${baht(quote.depositDue)} authorized — booking confirmed.`,
        language: 'en-TH',
        at,
        read: true
      });
    }
  }, [appendMessage, quotes, threads]);
  const requestChanges = useCallback((quoteId: string, note: string) => {
    const quote = quotes.find(q => q.id === quoteId);
    if (!quote) return;
    const thread = threads.find(t => t.bookingId === quote.bookingId);
    if (thread) sendMessage(thread.id, note, 'customer');
  }, [quotes, sendMessage, threads]);
  const declineQuote = useCallback((quoteId: string, reason: string) => {
    const quote = quotes.find(q => q.id === quoteId);
    if (!quote) return;
    const at = new Date().toISOString();
    setQuotes(prev => prev.map(q => q.id === quoteId ? {
      ...q,
      status: 'declined'
    } : q));
    setBookings(prev => prev.map(b => b.id === quote.bookingId ? {
      ...b,
      status: 'declined',
      timeline: [...b.timeline, {
        status: 'declined',
        at,
        note: reason
      }]
    } : b));
  }, [quotes]);
  const createRequest = useCallback((input: RequestInput) => {
    const at = new Date().toISOString();
    const bookingId = uid('bk');
    const threadId = uid('t');
    const booking: Booking = {
      id: bookingId,
      ref: `PA-${Math.floor(3000 + Math.random() * 900)}`,
      chefId: input.chefId,
      customerId: customer.id,
      status: 'requested',
      occasion: input.occasion,
      date: input.date,
      time: input.time,
      guests: input.guests,
      fulfillment: input.fulfillment,
      addressLine: `${customer.addressLine}, ${customer.city}`,
      createdAt: at,
      groceryReceipts: [],
      timeline: [{
        status: 'requested',
        at,
        note: input.note || undefined
      }]
    };
    const thread: Thread = {
      id: threadId,
      chefId: input.chefId,
      customerId: customer.id,
      bookingId,
      subject: `${input.occasion} · ${input.guests} guests`,
      lastAt: at,
      unreadForCustomer: 0,
      unreadForChef: 2,
      messages: [{
        id: uid('m'),
        threadId,
        role: 'system',
        kind: 'system',
        body: `${customer.name} requested a quote for ${input.occasion} · ${input.guests} guests · ${input.fulfillment === 'in_home' ? 'cooking at their place' : 'cook and deliver'}.`,
        language: 'en-TH',
        at,
        read: true
      }, ...(input.note ? [{
        id: uid('m'),
        threadId,
        role: 'customer' as const,
        kind: 'text' as const,
        body: input.note,
        language: 'en-TH',
        at,
        read: true
      }] : [])]
    };
    setBookings(prev => [booking, ...prev]);
    setThreads(prev => [thread, ...prev]);
    return {
      threadId,
      bookingId
    };
  }, []);
  const markRead = useCallback((threadId: string, role: 'chef' | 'customer') => {
    setThreads(prev => prev.map(t => t.id === threadId ? {
      ...t,
      unreadForChef: role === 'chef' ? 0 : t.unreadForChef,
      unreadForCustomer: role === 'customer' ? 0 : t.unreadForCustomer
    } : t));
  }, []);
  const toggleSaved = useCallback((chefId: string) => {
    setSavedChefIds(prev => prev.includes(chefId) ? prev.filter(id => id !== chefId) : [...prev, chefId]);
  }, []);
  const createTicket = useCallback((input: {
    subject: string;
    category: TicketCategory;
    body: string;
    bookingRef?: string;
  }) => {
    const at = new Date().toISOString();
    const id = uid('tk');
    const ticket: Ticket = {
      id,
      ref: `SUP-${Math.floor(4600 + Math.random() * 99)}`,
      subject: input.subject,
      category: input.category,
      status: 'open',
      createdAt: at,
      updatedAt: at,
      bookingRef: input.bookingRef,
      messages: [{
        id: uid('tm'),
        from: 'you',
        author: customer.name,
        body: input.body,
        at
      }]
    };
    setTickets(prev => [ticket, ...prev]);
    return id;
  }, []);
  const replyTicket = useCallback((ticketId: string, body: string) => {
    const at = new Date().toISOString();
    setTickets(prev => prev.map(t => t.id === ticketId ? {
      ...t,
      status: 'open',
      updatedAt: at,
      messages: [...t.messages, {
        id: uid('tm'),
        from: 'you',
        author: customer.name,
        body,
        at
      }]
    } : t));
  }, []);
  const cancelBooking = useCallback((bookingId: string, reason: string, refund: number) => {
    const at = new Date().toISOString();
    setBookings(prev => prev.map(b => b.id === bookingId ? {
      ...b,
      status: 'cancelled',
      cancellation: {
        reason,
        refund,
        at
      },
      timeline: [...b.timeline, {
        status: 'cancelled',
        at,
        note: `${baht(refund)} refunded`
      }]
    } : b));
  }, []);
  const addReceipt = useCallback((bookingId: string, label: string, amount: number) => {
    setBookings(prev => prev.map(b => b.id === bookingId ? {
      ...b,
      groceryReceipts: [...b.groceryReceipts, {
        id: uid('gr'),
        label,
        amount,
        uploadedAt: new Date().toISOString()
      }]
    } : b));
  }, []);
  const value = useMemo<AppDataValue>(() => ({
    threads,
    bookings,
    quotes,
    tickets,
    savedChefIds,
    threadById: id => threads.find(t => t.id === id),
    bookingById: id => bookings.find(b => b.id === id),
    quoteById: id => quotes.find(q => q.id === id),
    quoteVersions: bookingId => quotes.filter(q => q.bookingId === bookingId).sort((a, b) => a.version - b.version),
    sendMessage,
    sendQuote,
    acceptQuote,
    requestChanges,
    declineQuote,
    createRequest,
    markRead,
    toggleSaved,
    createTicket,
    replyTicket,
    cancelBooking,
    addReceipt
  }), [threads, bookings, quotes, tickets, savedChefIds, sendMessage, sendQuote, acceptQuote, requestChanges, declineQuote, createRequest, markRead, toggleSaved, createTicket, replyTicket, cancelBooking, addReceipt]);
  return <AppDataContext.Provider value={value} data-id="element-1000" data-mp="32zng6mb8wwwn">{children}</AppDataContext.Provider>;
}
export function useAppData(): AppDataValue {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData must be used inside AppDataProvider');
  return ctx;
}